import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  RichTextContent,
  useCollectionBinding,
  useContentToken,
  useTokens,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio, getContrastTextColor } from '../utils/luminance';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

function photoUrl(photo: GalleryPhoto): string {
  return photo.image || photo.original_url || '';
}

// ============================================================================
// Photo slider
// ============================================================================

const SLIDE_INTERVAL = 7000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const visible = photos.filter((photo) => photoUrl(photo));
  const count = visible.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent((prev) => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {visible.map((photo, i) => (
        <VibesitesImage
          key={photoUrl(photo) || i}
          url={photoUrl(photo)}
          alt={photo.alt_text || photo.title || heading}
          sizes="(min-width: 768px) 50vw, 100vw"
          className={`amenities-slider-img${i === current ? ' amenities-slider-img--active' : ''}`}
        />
      ))}

      {count > 1 && (
        <>
          <button
            type="button"
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            type="button"
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Column
// ============================================================================

function AmenityBulletList({ items }: { items: Amenity[] }) {
  return (
    <ul className="amenities-col-list">
      {items.map((item, i) => (
        <li key={`${item.title || 'amenity'}-${i}`} className="amenities-col-item">
          {item.title || ''}
        </li>
      ))}
    </ul>
  );
}

interface AmenityColumnProps {
  headingNode: ReactNode;
  listNode: ReactNode;
  imageNode: ReactNode;
  hasImage: boolean;
  imagePosition: 'left' | 'right';
  variant: 'community' | 'interior';
}

function AmenityColumn({
  headingNode,
  listNode,
  imageNode,
  hasImage,
  imagePosition,
  variant,
}: AmenityColumnProps) {
  const colRef = useScrollReveal<HTMLDivElement>();

  const contentPanel = (
    <div className="amenities-row-content">
      {headingNode}
      {listNode}
    </div>
  );

  const imagePanel = (
    <div className="amenities-row-image">
      {imageNode}
    </div>
  );

  return (
    <div
      ref={colRef}
      className={`reveal-fade-up amenities-row amenities-row--${variant} amenities-row--img-${imagePosition}${hasImage ? '' : ' amenities-row--no-image'}`}
    >
      <div className="amenities-row-inner">
        {imagePosition === 'left' ? (
          <>
            {hasImage ? imagePanel : null}
            {contentPanel}
          </>
        ) : (
          <>
            {contentPanel}
            {hasImage ? imagePanel : null}
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Heading color on the Interior Amenities (secondary) surface.
 * Keep primary when it clears WCAG AA (4.5:1) against secondary; otherwise
 * fall back to white/dark via getContrastTextColor so the heading stays
 * readable as brand palettes swap.
 */
function useInteriorHeadingColor(): string {
  const tokens = useTokens();
  return useMemo(() => {
    const secondary = tokens?.color?.secondary;
    const primary = tokens?.color?.primary;
    if (primary && secondary && contrastRatio(primary, secondary) >= 4.5) {
      return primary;
    }
    return getContrastTextColor(secondary);
  }, [tokens]);
}

export function AmenitiesList() {
  const communityHeadingRaw = useContentToken('amenitieslist.communityHeading');
  const apartmentHeadingRaw = useContentToken('amenitieslist.apartmentHeading');
  const communityHeadingText = stripHtml(communityHeadingRaw) || 'Community Amenities';
  const apartmentHeadingText = stripHtml(apartmentHeadingRaw) || 'Interior Amenities';
  const interiorHeadingColor = useInteriorHeadingColor();

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  useRevealStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;

  return (
    <section className="amenities-section bg-[var(--vibe-color-background)]">
      {hasContent ? (
        <div className="amenities-stack">
          {communityItems.length > 0 && (
            <AmenityColumn
              variant="community"
              headingNode={
                <RichTextContent
                  path="amenitieslist.communityHeading"
                  label="Community Heading"
                  defaultValue="<p>Community Amenities</p>"
                  as="h3"
                  className="amenities-col-heading"
                />
              }
              listNode={
                <EditableCollectionBinding bindingPath="amenitieslist.communityItems" label="Community Amenities">
                  <AmenityBulletList items={communityItems} />
                </EditableCollectionBinding>
              }
              imageNode={
                <EditableCollectionBinding bindingPath="amenitieslist.communityPhotos" label="Community Photos">
                  <PhotoSlider photos={communityPhotos} heading={communityHeadingText} />
                </EditableCollectionBinding>
              }
              hasImage={communityPhotos.some((photo) => photoUrl(photo))}
              imagePosition="right"
            />
          )}
          {apartmentItems.length > 0 && (
            <AmenityColumn
              variant="interior"
              headingNode={
                <RichTextContent
                  path="amenitieslist.apartmentHeading"
                  label="Apartment Heading"
                  defaultValue="<p>Interior Amenities</p>"
                  as="h3"
                  className="amenities-col-heading"
                  style={{ color: interiorHeadingColor }}
                />
              }
              listNode={
                <EditableCollectionBinding bindingPath="amenitieslist.apartmentItems" label="Apartment Features">
                  <AmenityBulletList items={apartmentItems} />
                </EditableCollectionBinding>
              }
              imageNode={
                <EditableCollectionBinding bindingPath="amenitieslist.apartmentPhotos" label="Apartment Photos">
                  <PhotoSlider photos={apartmentPhotos} heading={apartmentHeadingText} />
                </EditableCollectionBinding>
              }
              hasImage={apartmentPhotos.some((photo) => photoUrl(photo))}
              imagePosition="left"
            />
          )}
        </div>
      ) : (
        <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Amenities will be displayed here.</p>
        </div>
      )}

      <style>{`
        .amenities-section {
          width: 100%;
        }

        .amenities-stack {
          display: flex;
          flex-direction: column;
        }

        .amenities-row {
          position: relative;
          overflow: hidden;
        }

        .amenities-row-inner {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          align-items: center;
        }

        .amenities-row--no-image .amenities-row-inner {
          grid-template-columns: 1fr;
        }

        .amenities-row--community {
          background: var(--vibe-color-background);
          color: var(--vibe-color-text);
        }

        .amenities-row--interior {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
        }

        .amenities-row-content {
          min-width: 0;
        }

        .amenities-row-image {
          min-width: 0;
        }

        /* Community: list left, inset slider right */
        .amenities-row--community .amenities-row-content {
          padding: 5.5rem clamp(1.5rem, 6vw, 7.25rem) 4.5rem clamp(1.5rem, 8vw, 7.25rem);
        }
        .amenities-row--community .amenities-row-image {
          padding: 5.875rem 2.5rem 6.25rem 0;
        }
        .amenities-row--community .amenities-slider {
          height: 445px;
        }

        /* Interior: slider left, list right */
        .amenities-row--interior .amenities-row-content {
          padding: 5.5rem clamp(1.5rem, 8vw, 7.25rem) 4.5rem clamp(1.5rem, 5vw, 4rem);
        }
        .amenities-row--interior .amenities-row-image {
          padding: 2.875rem clamp(1.5rem, 8vw, 7.25rem) 4.375rem clamp(1.5rem, 8vw, 7.25rem);
        }
        .amenities-row--interior .amenities-slider {
          height: 391px;
        }

        @media (max-width: 992px) {
          .amenities-row--community .amenities-row-content,
          .amenities-row--interior .amenities-row-content {
            padding: 3.5rem 2.5rem 3rem;
          }
          .amenities-row--community .amenities-row-image,
          .amenities-row--interior .amenities-row-image {
            padding: 3rem 2.5rem;
          }
          .amenities-row--community .amenities-slider,
          .amenities-row--interior .amenities-slider {
            height: 360px;
          }
        }

        @media (max-width: 767px) {
          .amenities-row-inner {
            grid-template-columns: 1fr;
          }
          /* Image first on small screens, matching the demo Community stack. */
          .amenities-row--img-right > .amenities-row-inner > .amenities-row-content {
            order: 2;
          }
          .amenities-row--img-right > .amenities-row-inner > .amenities-row-image {
            order: 1;
          }
          .amenities-row--community .amenities-row-content,
          .amenities-row--interior .amenities-row-content {
            padding: 2.25rem 1.5rem 2.75rem;
          }
          .amenities-row--community .amenities-row-image,
          .amenities-row--interior .amenities-row-image {
            padding: 0;
          }
          .amenities-row--community .amenities-slider,
          .amenities-row--interior .amenities-slider {
            height: 240px;
          }
        }

        .amenities-slider {
          position: relative;
          width: 100%;
          overflow: hidden;
        }
        .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.6s ease;
        }
        .amenities-slider-img--active {
          opacity: 1;
        }

        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 40px;
          height: 40px;
          border: none;
          background: transparent;
          color: var(--vibe-color-text-on-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s;
          z-index: 2;
        }
        .amenities-slider:hover .amenities-slider-arrow,
        .amenities-slider:focus-within .amenities-slider-arrow {
          opacity: 0.85;
        }
        .amenities-slider-arrow:hover {
          opacity: 1;
        }
        .amenities-slider-arrow--prev { left: 8px; }
        .amenities-slider-arrow--next { right: 8px; }

        @media (hover: none) {
          .amenities-slider-arrow {
            opacity: 0.85;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slider-img {
            transition: none;
          }
        }

        .amenities-col-heading {
          font-family: var(--vibe-font-heading);
          font-size: 2.1875rem;
          font-weight: 400;
          color: var(--vibe-color-primary);
          text-align: left;
          margin: 0 0 1rem;
          line-height: 1;
        }

        .amenities-row--community .amenities-col-heading {
          color: var(--vibe-color-primary);
        }

        @media (max-width: 992px) {
          .amenities-col-heading {
            font-size: 1.75rem;
          }
        }
        @media (max-width: 767px) {
          .amenities-col-heading {
            font-size: 1.5rem;
          }
        }

        .amenities-col-list {
          list-style: disc;
          list-style-position: outside;
          padding: 0 0 0 1.25rem;
          margin: 1rem 0 0;
          max-width: 38rem;
        }

        .amenities-col-item {
          font-family: var(--vibe-font-body);
          font-size: 1.125rem;
          line-height: 1.7;
          padding: 0.1rem 0;
        }

        .amenities-row--community .amenities-col-item,
        .amenities-row--community .amenities-col-list {
          color: var(--vibe-color-text);
        }

        .amenities-row--interior .amenities-col-heading {
          color: var(--vibe-color-text-on-secondary);
        }
        .amenities-row--interior .amenities-col-heading :is(p, span, strong, em, a) {
          color: inherit;
        }

        .amenities-row--interior .amenities-col-item,
        .amenities-row--interior .amenities-col-list {
          color: var(--vibe-color-text-on-secondary);
        }

        @media (max-width: 767px) {
          .amenities-col-item {
            font-size: 1rem;
            line-height: 1.55;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Community Amenities</p>',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'richtext',
      defaultValue: '<p>Interior Amenities</p>',
    },
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesList;
